/**
 * Number → Words (Indian numbering system: Lakh/Crore)
 *
 * Used for the "Total In Words" line on generated invoices
 * (GenericInvoicePreview.vue) — e.g. 311434 -> "Three Lakh Eleven Thousand
 * Four Hundred Thirty Four". Kept as a standalone, dependency-free utility
 * (same convention as the other src/utils/invoice-*.js helpers) so it can be
 * reused anywhere else an amount needs to be spelled out (statements,
 * receipts, etc.) without pulling in a third-party numeral library.
 */

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
]
const TENS = [
  '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety',
]

function twoDigitsToWords(n) {
  if (n < 20) return ONES[n]
  const tens = Math.floor(n / 10)
  const ones = n % 10
  return TENS[tens] + (ones ? ` ${ONES[ones]}` : '')
}

function threeDigitsToWords(n) {
  const hundreds = Math.floor(n / 100)
  const rest = n % 100
  const parts = []
  if (hundreds) parts.push(`${ONES[hundreds]} Hundred`)
  if (rest) parts.push(twoDigitsToWords(rest))
  return parts.join(' ')
}

/**
 * Converts a non-negative integer into Indian-numbering words
 * (...Crore ...Lakh ...Thousand ...Hundred...). Returns 'Zero' for 0.
 */
export function integerToIndianWords(value) {
  let n = Math.floor(Math.abs(Number(value) || 0))
  if (n === 0) return 'Zero'

  const crore = Math.floor(n / 10000000); n %= 10000000
  const lakh = Math.floor(n / 100000); n %= 100000
  const thousand = Math.floor(n / 1000); n %= 1000
  const hundred = n

  const parts = []
  if (crore) parts.push(`${threeDigitsToWords(crore)} Crore`)
  if (lakh) parts.push(`${threeDigitsToWords(lakh)} Lakh`)
  if (thousand) parts.push(`${threeDigitsToWords(thousand)} Thousand`)
  if (hundred) parts.push(threeDigitsToWords(hundred))
  return parts.join(' ')
}

/**
 * Full "Total In Words" line for an invoice amount, e.g.
 * amountInWords(311434) -> "Three Lakh Eleven Thousand Four Hundred Thirty
 * Four Rupees Only", and amountInWords(23106.76) -> "...Rupees and
 * Seventy Six Paise Only".
 */
export function amountInWords(amount) {
  const n = Math.round((Number(amount) || 0) * 100) / 100
  const rupees = Math.floor(n)
  const paise = Math.round((n - rupees) * 100)

  let words = `${integerToIndianWords(rupees)} Rupee${rupees === 1 ? '' : 's'}`
  if (paise > 0) {
    words += ` and ${integerToIndianWords(paise)} Paise`
  }
  return `${words} Only`
}
